import { Col, Row } from "react-bootstrap";
import Button from "../../components/Buttons/Button";
import ItemImage from "../../../assets/images/productimage.png";
import TableCustomHeader from "../../components/table/TableCustomHeader";
import SimpleNumberField from "../../components/inputNumberField/SimpleNumberField";
import DropdownSkeleton from "../../Skeletons/DropdownSkeleton/DropdownSkeleton";
import {
  getShortestSku,
  getDurationString,
  getReceiptPrintDate,
  getFormattedCondition,
  parseToThousandSeperatorDecimalNumber,
  parseToThousandSeperatorWholeNumber,
  upsertEditedItemToLocalStorage,
  formatCurrency,
  getActionColumnOfReactDataTableComponent,
} from "../../utility";
import { ShowIcon, EditIcon } from "../../../assets/icons/iconsProvider";
import {
  setItemToLocalStorage,
  getItemFromLocalStorage,
} from "../../../system/storage";
import {
  EMPTY_STRING,
  inventoryConstants,
  buttonNameConstants,
  SUBCATEGORY,
  toolTipMessages,
} from "../../../constants";
import { BREAKDOWN_ENUMS } from "../../../system/globalEnums";
import {
  StatusTag,
  TableCustomCellWrapper,
  TitleToolTip,
} from "../../components";
import { render } from "@testing-library/react";

const getStockAuditStatusColumn = () => {
  return {
    id: "status",
    name: "Status",
    selector: (row) => row.status,
    sortable: true,
    sortDirection: null,
    cell: (row) => (
      <TableCustomCellWrapper row={row} isLoadingSkeleton={() => !row?.id}>
        <StatusTag status={row.status} />
      </TableCustomCellWrapper>
    ),
  };
};

const getStockAuditProductTypeNameColumn = () => {
  return {
    id: "productTypeName",
    name: "Type",
    selector: (row) => row.productTypeName,
    sortable: true,
    sortDirection: null,
    cellValueKey: "productTypeName",
    isLoadingSkeleton: (row) => !row?.id,
  };
};

const getStockAuditCategoryColumn = () => {
  return {
    id: "categoryName",
    name: "Category",
    selector: (row) => row.categoryName,
    sortable: true,
    sortDirection: null,
    cellValueKey: "categoryName",
    isLoadingSkeleton: (row) => !row?.id,
  };
};

const getStockAuditItemsAuditedColumn = () => {
  return {
    id: "itemsAudited",
    name: "# of Items Audited",
    selector: (row) => row.itemsAudited,
    sortable: true,
    sortDirection: null,
    cellValueKey: "itemsAudited",
    isLoadingSkeleton: (row) => !row?.id,
    isConvertValueToWholeNumber: true,
  };
};

const getStockAuditNoOfDiscrepanciesColumn = () => {
  return {
    id: "discrepancies",
    name: "# of Discrepancies",
    selector: (row) => row.discrepancies,
    sortable: true,
    sortDirection: null,
    cellValueKey: "discrepancies",
    isLoadingSkeleton: (row) => !row?.id,
    isConvertValueToWholeNumber: true,
  };
};

const getStockAuditQtyDiscrepancyColumn = (
  cellValueKey = "quantityDiscrepancy",
  name = "Qty Discrepancies"
) => {
  return {
    id: cellValueKey,
    name: name,
    selector: (row) => row[cellValueKey],
    sortable: false,
    sortDirection: null,
    cellValueKey: cellValueKey,
    isLoadingSkeleton: (row) => !row?.id,
    isApplyTextColorClassesForValues: true,
    isConvertValueToWholeNumber: true,
  };
};

const getStockAuditStockPriceDiscrepancyColumn = (
  cellValueKey = "stockPriceDiscrepancy",
  name = "Stock Price Discrepancies"
) => {
  return {
    id: cellValueKey,
    name: name,
    selector: (row) => row[cellValueKey],
    sortable: false,
    sortDirection: null,
    cellValueKey: cellValueKey,
    isLoadingSkeleton: (row) => !row?.id,
    isApplyTextColorClassesForValues: true,
    isConvertValueToDollarAmount: true,
  };
};

const getStockAuditCogsDiscrepancyColumn = (
  cellValueKey = "cogsDiscrepancy",
  name = "COGS Discrepancies"
) => {
  return {
    id: cellValueKey,
    name: name,
    selector: (row) => row[cellValueKey],
    sortable: false,
    sortDirection: null,
    cellValueKey: cellValueKey,
    isLoadingSkeleton: (row) => !row?.id,
    isApplyTextColorClassesForValues: true,
    isConvertValueToDollarAmount: true,
  };
};

const getStockAuditColumns = (handleViewClick, handleContinueClick) => [
  {
    id: "startDate",
    name: "Start Date",
    selector: (row) => row.startDate,
    sortable: true,
    cellValueKey: "startDate",
    isLoadingSkeleton: (row) => !row?.id,
    sortDirection: null,
  },
  {
    id: "endDate",
    name: "Completion Date",
    selector: (row) => row.endDate,
    sortable: true,
    sortDirection: null,
    cellValueKey: "endDate",
    isLoadingSkeleton: (row) => !row?.id,
  },
  {
    id: "duration",
    name: "Duration",
    selector: (row) => row.duration,
    sortable: false,
    sortDirection: null,
    cellValueKey: "duration",
    isLoadingSkeleton: (row) => !row?.id,
  },
  {
    id: "nickName",
    name: "Nickname",
    selector: (row) => row.nickName,
    sortable: true,
    sortDirection: null,
    cellValueKey: "nickName",
    isLoadingSkeleton: (row) => !row?.id,
  },
  getStockAuditStatusColumn(),
  getStockAuditItemsAuditedColumn(),
  getStockAuditNoOfDiscrepanciesColumn(),
  getStockAuditQtyDiscrepancyColumn(),
  getStockAuditStockPriceDiscrepancyColumn(),
  getStockAuditCogsDiscrepancyColumn(),
  getActionColumnOfReactDataTableComponent(
    (row) =>
      row?.id && (
        <>
          {row.status === buttonNameConstants.COMPLETED ? (
            <button
              type="button"
              onClick={() => handleViewClick(row)}
              className={`action-Button-Wrapper view-order-icon`}
            >
              <ShowIcon className="cursor-pointer" />
            </button>
          ) : row.status === buttonNameConstants.IN_PROGRESS ? (
            <Button
              handleClick={() => handleContinueClick(row)}
              label={buttonNameConstants.CONTINUE}
              className={"small-button"}
            />
          ) : (
            ""
          )}
        </>
      ),
    true
  ),
];

const getAuditExpandedRow = (props) => {
  const { data } = props; // <-- this is the full row data
  return (
    <div className="p-3 light-smoke-blue-bg-color">
      <Row>
        {/* Left side: 1/4 width */}
        <Col md={3}>
          <p>
            <strong>Started By:</strong> {data.startedBy}
          </p>
          <p>
            <strong>Completed By:</strong> {data.completedBy}
          </p>
          <p>
            <strong>Notes:</strong> {data.notes}
          </p>
        </Col>

        {/* Right side: 3/4 width */}
        <Col md={9}>
          <table className="table table-sm light-smoke-blue-bg-color-table">
            <thead>
              <tr>
                <th>
                  <strong>Metric</strong>
                </th>
                <th>
                  <strong>Expected</strong>
                </th>
                <th>
                  <strong>Counted</strong>
                </th>
                <th>
                  <strong>Discrepancy</strong>
                </th>
                <th>
                  <strong>Discrepancy %</strong>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.expandedData.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.metric}</td>
                  <td>
                    {item.metric === "Quantity"
                      ? parseToThousandSeperatorWholeNumber(item.expected)
                      : `${formatCurrency(item.expected?.toFixed(2))}`}
                  </td>
                  <td>
                    {item.metric === "Quantity"
                      ? parseToThousandSeperatorWholeNumber(item.counted)
                      : `${formatCurrency(item.counted?.toFixed(2))}`}
                  </td>
                  <td>
                    {" "}
                    <span
                      className={
                        item.discrepancy < 0
                          ? "text-red"
                          : item.discrepancy > 0
                          ? "text-purple"
                          : ""
                      }
                    >
                      {item.metric === "Quantity"
                        ? parseToThousandSeperatorWholeNumber(item.discrepancy)
                        : `${formatCurrency(item.discrepancy)}`}
                    </span>
                  </td>
                  <td>
                    <span
                      className={
                        item.discrepancy < 0
                          ? "text-red"
                          : item.discrepancy > 0
                          ? "text-purple"
                          : ""
                      }
                    >
                      {item.discrepancyPercent + "%"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Col>
      </Row>
    </div>
  );
};

const getStockAuditBreakdownExpandedRow = (props) => {
  const { data, breakdownBy } = props;
  const expandedData = data.expandedData;

  return (
    <div className="p-3 light-smoke-blue-bg-color">
      <Row>
        {/* Left side: 1/4 width */}
        {breakdownBy === BREAKDOWN_ENUMS.CATEGORY.value && (
          <Col md={3}>
            <p>
              <strong>Start Date:</strong>{" "}
              {getReceiptPrintDate(data?.startDate, true)}
            </p>
            <p>
              <strong>Completion Date:</strong>{" "}
              {getReceiptPrintDate(data?.endDate, true)}
            </p>
            <p>
              <strong>Duration:</strong>{" "}
              {getDurationString(data?.startDate, data?.endDate)}
            </p>
            <p>
              <strong>Started By:</strong> {data.startedBy}
            </p>
            <p>
              <strong>Completed By:</strong> {data.completedBy}
            </p>
          </Col>
        )}

        {/* Right side: 3/4 width */}
        <Col>
          <table className="table table-sm light-smoke-blue-bg-color-table">
            <thead>
              <tr>
                <th>
                  <strong>Metric</strong>
                </th>
                <th>
                  <strong>Expected</strong>
                </th>
                <th>
                  <strong>Counted</strong>
                </th>
                <th>
                  <strong>Discrepancy</strong>
                </th>
                <th>
                  <strong>Discrepancy %</strong>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.expandedData.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.metric}</td>
                  <td>
                    {item.metric === "Quantity"
                      ? parseToThousandSeperatorWholeNumber(item.expected)
                      : `${formatCurrency(item.expected?.toFixed(2))}`}
                  </td>
                  <td>
                    {item.metric === "Quantity"
                      ? parseToThousandSeperatorWholeNumber(item.counted)
                      : `${formatCurrency(item.counted?.toFixed(2))}`}
                  </td>
                  <td>
                    {" "}
                    <span
                      className={
                        item.discrepancy < 0
                          ? "text-red"
                          : item.discrepancy > 0
                          ? "text-purple"
                          : ""
                      }
                    >
                      {item.metric === "Quantity"
                        ? parseToThousandSeperatorWholeNumber(item.discrepancy)
                        : `${formatCurrency(item.discrepancy)}`}
                    </span>
                  </td>
                  <td>
                    <span
                      className={
                        item.discrepancy < 0
                          ? "text-red"
                          : item.discrepancy > 0
                          ? "text-purple"
                          : ""
                      }
                    >
                      {item.discrepancyPercent + "%"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Col>
      </Row>
    </div>
  );
};

const convertExpandedDataToDisplayable = (item) => {
  return [
    {
      metric: "Quantity",
      expected: item.quantityExpected,
      counted: item.quantityCounted,
      discrepancy: parseFloat(
        (item.quantityCounted - item.quantityExpected).toFixed(2)
      ),
      discrepancyPercent: item.quantityExpected
        ? parseFloat(
            ((item.quantityCounted - item.quantityExpected) * 100) /
              item.quantityExpected
          ).toFixed(2)
        : 0,
    },
    {
      metric: "Stock Price",
      expected: item.stockPriceExpected,
      counted: item.stockPriceCounted,
      discrepancy: parseFloat(
        (item.stockPriceCounted - item.stockPriceExpected).toFixed(2)
      ),
      discrepancyPercent: item.stockPriceExpected
        ? parseFloat(
            ((item.stockPriceCounted - item.stockPriceExpected) * 100) /
              item.stockPriceExpected
          ).toFixed(2)
        : 0,
    },
    {
      metric: "COGS",
      expected: item.cogsExpected,
      counted: item.cogsCounted,
      discrepancy: parseFloat(
        (item.cogsCounted - item.cogsExpected).toFixed(2)
      ),
      discrepancyPercent: item.cogsExpected
        ? parseFloat(
            ((item.cogsCounted - item.cogsExpected) * 100) / item.cogsExpected
          ).toFixed(2)
        : 0,
    },
  ];
};

const convertDataToDisplayable = (data) => {
  if (!data || !Array.isArray(data)) return [];

  return data.map((item) => ({
    unconvertedData: item,
    id: item.id,
    startDate: getReceiptPrintDate(item.startDate),
    endDate: item.endDate ? getReceiptPrintDate(item.endDate) : EMPTY_STRING,
    duration: getDurationString(item.startDate, item.endDate),
    nickName: item.nickName,
    status: item.status,
    itemsAudited: item.itemsAudited,
    discrepancies: item.discrepancies,
    quantityDiscrepancy: item.quantityCounted - item.quantityExpected,
    stockPriceDiscrepancy: parseFloat(
      (item.stockPriceCounted - item.stockPriceExpected).toFixed(2)
    ),
    cogsDiscrepancy: parseFloat(
      (item.cogsCounted - item.cogsExpected).toFixed(2)
    ),
    startedBy: item.startedBy,
    completedBy: item.completedBy,
    notes: item.notes,
    expandedData: convertExpandedDataToDisplayable(item),
  }));
};

const getStockAuditCategoriesColumns = (handleOpenClick) => [
  getStockAuditProductTypeNameColumn(),
  getStockAuditCategoryColumn(),
  getStockAuditStatusColumn(),
  {
    id: "itemsAudited",
    name: "Total Counted Items",
    selector: (row) => row.itemsAudited,
    sortable: true,
    sortDirection: null,
    cellValueKey: "itemsAudited",
    isLoadingSkeleton: (row) => !row?.id,
    isConvertValueToWholeNumber: true,
  },
  {
    id: "totalItems",
    name: "Total Items",
    selector: (row) => row.totalItems,
    sortable: true,
    sortDirection: null,
    cellValueKey: "totalItems",
    isLoadingSkeleton: (row) => !row?.id,
    isConvertValueToWholeNumber: true,
  },
  {
    id: "totalQuantity",
    name: "Total Quantity",
    selector: (row) => row.totalQuantity,
    sortable: true,
    sortDirection: null,
    cellValueKey: "totalQuantity",
    isLoadingSkeleton: (row) => !row?.id,
    isConvertValueToWholeNumber: true,
  },
  {
    id: "totalStockPrice",
    name: "Total Stock Price",
    selector: (row) => row.totalStockPrice,
    sortable: true,
    sortDirection: null,
    cellValueKey: "totalStockPrice",
    isLoadingSkeleton: (row) => !row?.id,
    isConvertValueToDollarAmount: true,
  },
  {
    id: "totalCogs",
    name: "Total COGS",
    selector: (row) => row.totalCogs,
    sortable: true,
    sortDirection: null,
    cellValueKey: "totalCogs",
    isLoadingSkeleton: (row) => !row?.id,
    isConvertValueToDollarAmount: true,
  },
  getActionColumnOfReactDataTableComponent(
    (row) =>
      row?.id && (
        <div className="d-flex justify-content-center align-items-center w-100">
          <button
            className="action-Button-Wrapper ps-0 pe-0"
            onClick={() => {
              handleOpenClick(row);
            }}
          >
            <EditIcon className="cursor-pointer text-muted" />
          </button>
        </div>
      )
  ),
];

const getStockAuditInventoryColumns = (
  rowBeingEditRef,
  setGridData,
  currentStatus
) => [
  {
    headerName: "Sku",
    lockPosition: "left",
    field: "sku",
    cellRenderer: (row) => {
      const shortestSku = getShortestSku(row.data.sku);
      return row?.data?.sku ? (
        <div className="d-flex justify-content-start align-items-center">
          <div className="product-image">
            <img src={row.data.imgUrl || ItemImage} alt="" />
          </div>
          <span className="inventory-cell-max-size product-sku">
            <TableCustomHeader
              id={`sku-number-in-array-${shortestSku}`}
              label={
                <span className="inventory-cell-max-size product-sku">
                  {shortestSku.length <= 6
                    ? shortestSku
                    : shortestSku.slice(-6) + "..."}
                </span>
              }
              tooltopText={
                <div className="d-flex flex-column ">
                  {row.data.sku.map((s, index) => (
                    <p
                      className="sku-wrapper text-nowrap text-start fw-normal m-0"
                      key={index}
                    >
                      {s}
                    </p>
                  ))}
                </div>
              }
              className="sku-tooltip"
            />
          </span>
        </div>
      ) : (
        <DropdownSkeleton width="5em" height="1.5em" />
      );
    },
  },
  {
    headerName: "Title",
    field: "product_name",
    cellRenderer: (row) =>
      row?.data?.product_name ? (
        <span className="inventory-cell-max-size">
          <TableCustomHeader
            id={`product-name-in-array-${getShortestSku(row.data.sku)}`}
            label={
              <span className="inventory-cell-max-size">
                {row.data.product_name}
              </span>
            }
            tooltopText={
              <p className="text-start fw-normal m-0">
                {row.data.product_name}
              </p>
            }
            className="sku-tooltip"
          />
        </span>
      ) : (
        <DropdownSkeleton width="5em" height="1.5em" />
      ),
    cellStyle: {
      overflow: "visible",
    },
  },
  {
    headerName: "Condition",
    field: "price.type",
    cellRenderer: (params) =>
      params?.data?.price?.type ? (
        <span className="inventory-cell-max-size">
          {getFormattedCondition(params?.data)}
        </span>
      ) : (
        <DropdownSkeleton width="5em" height="1.5em" />
      ),
  },
  {
    headerName: "Tags",
    field: "tags",
    cellRenderer: (row) =>
      row?.data?.tags ? (
        row.data.tags?.filter(
          (tag) => tag !== inventoryConstants.CUSTOM_ITEM_TAG
        )?.length || 0
      ) : (
        <DropdownSkeleton width="5em" height="1.5em" />
      ),
  },
  {
    headerName: "COGS",
    field: "price.unit_purchase_price",
    suppressKeyboardEvent: (params) => {
      const e = params.event;
      // Only suppress arrow key handling when editing inside an input
      if (
        e.target.tagName === "INPUT" &&
        ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)
      ) {
        return true; // stop grid from handling arrow keys
      }

      return false;
    },
    cellRenderer: (params) => {
      return params.data?.price ? (
        <span className="inventory-cell-max-size">
          $
          {parseToThousandSeperatorDecimalNumber(
            params.data.price.unit_purchase_price
          )}
        </span>
      ) : (
        <DropdownSkeleton width="5em" height="1.5em" />
      );
    },
  },
  {
    headerName: "Stock Price",
    field: "price.unit_sell_price",
    suppressKeyboardEvent: (params) => {
      const e = params.event;
      // Only suppress arrow key handling when editing inside an input
      if (
        e.target.tagName === "INPUT" &&
        ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)
      ) {
        return true; // stop grid from handling arrow keys
      }

      return false;
    },
    cellRenderer: (params) => {
      return params.data?.price ? (
        <span className="inventory-cell-max-size">
          $
          {parseToThousandSeperatorDecimalNumber(
            params.data.price.unit_sell_price
          )}
        </span>
      ) : (
        <DropdownSkeleton width="5em" height="1.5em" />
      );
    },
  },
  {
    headerName: "Expected Qty",
    field: "price.quantity",
    cellRenderer: (params) => {
      return params.data?.price ? (
        <span className="inventory-cell-max-size">
          {parseToThousandSeperatorWholeNumber(
            Number(params.data.price.quantity || 0) +
              Number(params.data.price.onlineQuantity || 0)
          )}
        </span>
      ) : (
        <DropdownSkeleton width="5em" height="1.5em" />
      );
    },
  },
  {
    headerName: "Counted Qty",
    field: "price.countedQuantity",
    cellRenderer: (params) => {
      return params.data?.price ? (
        params.data.id === rowBeingEditRef?.current.id &&
        currentStatus !== buttonNameConstants.COMPLETED ? (
          <SimpleNumberField
            className="discount-number-field"
            value={params.data.price.countedQuantity ?? ""}
            maxValue={
              params.data?.serialNumber
                ? 1
                : inventoryConstants.THRESHOLD_MAX_CASH_VALUE
            }
            onChange={(e) => {
              rowBeingEditRef.current = updateCountedQuantityOfObject(
                rowBeingEditRef.current,
                Math.round(e)
              );
              // Update the grid
              setGridData((prevData) =>
                prevData.map((row) => {
                  if (row.id === params.data.id) {
                    const prevCountedQuantity = row.price?.countedQuantity;

                    // Check if it was previously not set
                    if (prevCountedQuantity == null) {
                      // increment localStorage counter
                      const storedCount =
                        parseInt(
                          getItemFromLocalStorage(
                            inventoryConstants.STOCK_AUDIT_INVENTORY_UPDATED_COUNT
                          )
                        ) || 0;
                      setItemToLocalStorage(
                        inventoryConstants.STOCK_AUDIT_INVENTORY_UPDATED_COUNT,
                        storedCount + 1
                      );
                    }

                    const storedDelta =
                      parseInt(
                        getItemFromLocalStorage(
                          inventoryConstants.STOCK_AUDIT_INVENTORY_QUANTITY_DELTA
                        )
                      ) || 0;

                    setItemToLocalStorage(
                      inventoryConstants.STOCK_AUDIT_INVENTORY_QUANTITY_DELTA,
                      storedDelta + (Math.round(e) - (prevCountedQuantity || 0))
                    );

                    // Return updated row
                    return updateCountedQuantityOfObject(row, Math.round(e));
                  }
                  return row;
                })
              );

              upsertEditedItemToLocalStorage(
                getLocalStorageStockAuditObject(params.data.id, Math.round(e)),
                inventoryConstants.STOCK_AUDIT
              );
            }}
          />
        ) : (
          <span className="inventory-cell-max-size">
            {params.data.price.countedQuantity != null
              ? parseToThousandSeperatorWholeNumber(
                  params.data.price.countedQuantity
                )
              : ""}
          </span>
        )
      ) : (
        <DropdownSkeleton width="5em" height="1.5em" />
      );
    },
  },
];

const getStockAuditBreakdownColumns = (selectedBreakdown) => {
  // Early return for ITEMS breakdown
  if (selectedBreakdown === BREAKDOWN_ENUMS.ITEM.value) {
    return [
      {
        id: "productType",
        name: "Type",
        selector: (row) => row.productType,
        sortable: true,
        sortDirection: null,
        cell: (row) =>
          row?.productType ? (
            <TableCustomHeader
              id={`type-tooltip-${row.id}`}
              label={<span>{row.productType}</span>}
              tooltopText={<span>{row.productType}</span>}
              className="sku-tooltip"
            />
          ) : (
            <DropdownSkeleton width="5em" height="1.5em" />
          ),
      },
      {
        id: "category_name",
        name: "Category",
        selector: (row) => row.category_name,
        sortable: true,
        sortDirection: null,
        cell: (row) =>
          row?.category_name ? (
            <TableCustomHeader
              id={`category-tooltip-${row.id}`}
              label={
                <span className="inventory-cell-max-size">
                  {row.category_name?.length > 15
                    ? `${row.category_name.slice(0, 15)}...`
                    : row.category_name}
                </span>
              }
              tooltopText={<span>{row.category_name}</span>}
              className="sku-tooltip"
            />
          ) : (
            <DropdownSkeleton width="5em" height="1.5em" />
          ),
      },
      {
        id: "subcategory",
        name: "SubCategory",
        selector: (row) => row.subcategory,
        sortable: true,
        sortDirection: null,
        cell: (row) =>
          row?.id ? (
            <TableCustomHeader
              id={`subcategory-tooltip-${row.id}`}
              label={
                <span className="inventory-cell-max-size">
                  {row.subcategory?.length > 12
                    ? `${row.subcategory.slice(0, 12)}...`
                    : row.subcategory || SUBCATEGORY.NONE}
                </span>
              }
              tooltopText={<span>{row.subcategory || SUBCATEGORY.NONE}</span>}
              className="sku-tooltip"
            />
          ) : (
            <DropdownSkeleton width="5em" height="1.5em" />
          ),
      },
      {
        id: "title",
        name: "Title",
        selector: (row) => row.product_name,
        sortable: true,
        sortDirection: null,
        cell: (row) =>
          row?.product_name ? (
            <TableCustomHeader
              label={
                <span>
                  {row.product_name?.length > 10
                    ? `${row.product_name.slice(0, 10)}...`
                    : row.product_name}
                </span>
              }
              tooltopText={<span>{row.product_name}</span>}
              id={`tooltip-${row.id}`}
              className="sku-tooltip"
            />
          ) : (
            <DropdownSkeleton width="5em" height="1.5em" />
          ),
      },
      {
        id: "expectedQuantity",
        name: "Expected Quantity",
        selector: (row) => row.expectedQuantity,
        sortable: true,
        sortDirection: null,
        cell: (row) =>
          row.expectedQuantity || row.expectedQuantity === 0 ? (
            parseToThousandSeperatorWholeNumber(row.expectedQuantity)
          ) : (
            <DropdownSkeleton width="4em" height="1.5em" />
          ),
      },
      {
        id: "price.countedQuantity",
        name: "Counted Qty",
        selector: (row) => row.price?.countedQuantity,
        sortable: true,
        sortDirection: null,
        cell: (row) =>
          row.price?.countedQuantity || row.price?.countedQuantity === 0 ? (
            parseToThousandSeperatorWholeNumber(row.price?.countedQuantity)
          ) : (
            <DropdownSkeleton width="4em" height="1.5em" />
          ),
      },
      {
        id: "stockPrice",
        name: "Stock Price",
        selector: (row) => row.stockPrice,
        sortable: true,
        sortDirection: null,
        cell: (row) =>
          row?.stockPrice || row.stockPrice === 0 ? (
            formatCurrency(row?.stockPrice)
          ) : (
            <DropdownSkeleton width="4em" height="1.5em" />
          ),
      },
      {
        id: "cogs",
        name: "COGS",
        selector: (row) => row.cogs,
        sortable: true,
        sortDirection: null,
        cell: (row) =>
          row?.cogs || row.cogs === 0 ? (
            formatCurrency(row?.cogs)
          ) : (
            <DropdownSkeleton width="4em" height="1.5em" />
          ),
      },
      {
        id: "qtyDiscrepancy",
        name: "Qty Discrepancy",
        selector: (row) => row?.qtyDiscrepancy,
        sortable: true,
        sortDirection: null,
        cell: (row) =>
          row?.qtyDiscrepancy || row.qtyDiscrepancy === 0 ? (
            <span
              className={
                row.qtyDiscrepancy < 0
                  ? "text-red"
                  : row.qtyDiscrepancy > 0
                  ? "text-purple"
                  : ""
              }
            >
              {parseToThousandSeperatorWholeNumber(row.qtyDiscrepancy)}
            </span>
          ) : (
            <DropdownSkeleton width="4em" height="1.5em" />
          ),
      },
      {
        id: "stockPriceDiscrepancy",
        name: "Stock Price Discrepancy",
        selector: (row) => row.stockPriceDiscrepancy,
        sortable: true,
        sortDirection: null,
        cell: (row) =>
          row?.stockPriceDiscrepancy || row.stockPriceDiscrepancy === 0 ? (
            <span
              className={
                row.stockPriceDiscrepancy < 0
                  ? "text-red"
                  : row.stockPriceDiscrepancy > 0
                  ? "text-purple"
                  : ""
              }
            >
              {formatCurrency(row?.stockPriceDiscrepancy)}
            </span>
          ) : (
            <DropdownSkeleton width="4em" height="1.5em" />
          ),
      },
      {
        id: "cogsDiscrepancy",
        name: "COGS Discrepancy",
        selector: (row) => row.cogsDiscrepancy,
        sortable: true,
        style: {
          justifyContent: "flex-start !important",
        },
        sortDirection: null,
        cell: (row) =>
          row?.cogsDiscrepancy || row.cogsDiscrepancy === 0 ? (
            <span
              className={
                row.cogsDiscrepancy < 0
                  ? "text-red"
                  : row.cogsDiscrepancy > 0
                  ? "text-purple"
                  : ""
              }
            >
              {formatCurrency(row?.cogsDiscrepancy)}
            </span>
          ) : (
            <DropdownSkeleton width="4em" height="1.5em" />
          ),
      },
    ];
  }

  return [
    getStockAuditProductTypeNameColumn(),
    selectedBreakdown !== BREAKDOWN_ENUMS.TYPE.value &&
      getStockAuditCategoryColumn(),
    selectedBreakdown === BREAKDOWN_ENUMS.SUBCATEGORY.value && {
      id: "SubCategory",
      name: "SubCategory",
      selector: (row) => row.subCategory,
      sortable: true,
      sortDirection: null,
      cellValueKey: "subCategory",
      cellValueFallBack: SUBCATEGORY.NONE,
      isLoadingSkeleton: (row) => !row?.id,
    },
    getStockAuditItemsAuditedColumn(),
    getStockAuditNoOfDiscrepanciesColumn(),
    getStockAuditQtyDiscrepancyColumn("qtyDiscrepancy", "Qty Discrepancy"),
    getStockAuditStockPriceDiscrepancyColumn(
      "stockPriceDiscrepancy",
      "Stock Price Discrepancy"
    ),
    getStockAuditCogsDiscrepancyColumn("cogsDiscrepancy", "COGS Discrepancy"),
  ].filter(Boolean);
};

const getStockAuditBreakdownCSVColumns = (selectedBreakdown, data) => {
  if (selectedBreakdown === BREAKDOWN_ENUMS.ITEM.value) {
    return data.map((item) => ({
      Type: item.productType,
      Category: item.category_name,
      SubCategory: item.subcategory || "No subcategory",
      Title: item.product_name,
      "Expected Quantity": item.expectedQuantity ?? 0,
      "Counted Qty": item.price?.countedQuantity ?? 0,
      "Stock Price": formatCurrency(item.stockPrice ?? 0),
      COGS: formatCurrency(item.cogs ?? 0),
      "Qty Discrepancy": item.qtyDiscrepancy ?? 0,
      "Stock Price Discrepancy": item.stockPriceDiscrepancy ?? 0,
      "COGS Discrepancy": item.cogsDiscrepancy ?? 0,
    }));
  }
  if (selectedBreakdown === BREAKDOWN_ENUMS.TYPE.value) {
    return data.map((item) => ({
      Type: item.productTypeName,
      "# of Items Audited": item.itemsAudited ?? 0,
      "# of Discrepancies": item.discrepancies ?? 0,
      "Qty Discrepancy": item.qtyDiscrepancy ?? 0,
      "Stock Price Discrepancy": item.stockPriceDiscrepancy ?? 0,
      "COGS Discrepancy": item.cogsDiscrepancy ?? 0,
    }));
  }
  if (selectedBreakdown === BREAKDOWN_ENUMS.CATEGORY.value) {
    return data.map((item) => ({
      Type: item.productTypeName,
      Category: item.categoryName,
      "# of Items Audited": item.itemsAudited ?? 0,
      "# of Discrepancies": item.discrepancies ?? 0,
      "Qty Discrepancy": item.qtyDiscrepancy ?? 0,
      "Stock Price Discrepancy": item.stockPriceDiscrepancy ?? 0,
      "COGS Discrepancy": item.cogsDiscrepancy ?? 0,
    }));
  }
  if (selectedBreakdown === BREAKDOWN_ENUMS.SUBCATEGORY.value) {
    return data.map((item) => ({
      Type: item.productTypeName,
      Category: item.categoryName,
      SubCategory: item.subCategory,
      "# of Items Audited": item.itemsAudited ?? 0,
      "# of Discrepancies": item.discrepancies ?? 0,
      "Qty Discrepancy": item.qtyDiscrepancy ?? 0,
      "Stock Price Discrepancy": item.stockPriceDiscrepancy ?? 0,
      "COGS Discrepancy": item.cogsDiscrepancy ?? 0,
    }));
  }
};
const calculateDuration = (startDate, endDate) => {
  const diffMs = new Date(endDate) - new Date(startDate);
  const diffMins = Math.floor(diffMs / 60000);
  return `${diffMins} mins`;
};
const getStockAuditCSVWithSummary = (
  selectedBreakdown,
  data,
  stockAudit,
  stockAuditStatistics,
  stockAuditBreakdown
) => {
  const breakdownData = getStockAuditBreakdownCSVColumns(
    selectedBreakdown,
    data
  );

  const duration = calculateDuration(stockAudit.startDate, stockAudit.endDate);

  // Determine max columns needed from breakdown data
  const maxColumns =
    breakdownData.length > 0
      ? Math.max(...breakdownData.map((item) => Object.keys(item).length))
      : 6;

  // Helper to create empty row with correct number of columns
  const createEmptyRow = (firstCol = "") => {
    const row = { A: firstCol };
    for (let i = 1; i < maxColumns; i++) {
      row[String.fromCharCode(65 + i)] = "";
    }
    return row;
  };

  // Helper to create row with specific values
  const createRow = (values) => {
    const row = {};
    for (let i = 0; i < maxColumns; i++) {
      row[String.fromCharCode(65 + i)] = values[i] ?? "";
    }
    return row;
  };

  // Summary rows with dynamic column structure
  const summaryRows = [
    createRow(["STOCK AUDIT SUMMARY"]),
    createEmptyRow(),
    createRow(["Nick Name:", stockAudit.nickName || ""]),
    createRow([
      "Start Date:",
      new Date(stockAudit.startDate).toLocaleDateString(),
    ]),
    createRow(["End Date:", new Date(stockAudit.endDate).toLocaleDateString()]),
    createRow(["Start By:", stockAudit.startedBy]),
    createRow(["Duration:", duration]),
    createRow(["Notes:", stockAudit.notes || ""]),
    createRow([
      `${getBreakdownTotalLabel(selectedBreakdown)}`,
      stockAuditBreakdown?.totalRecords ?? 0,
    ]),
    createRow([
      "Largest Qty Diff:",
      `${stockAuditStatistics.largestQtyDiffProduct}  ${stockAuditStatistics.largestQtyDiff}`,
    ]),
    createRow([
      "Largest Price Diff:",
      `${stockAuditStatistics.largestPriceDiffProduct}  ${formatCurrency(
        stockAuditStatistics.largestPriceDiff,
        false,
        true
      )}`,
    ]),
    createEmptyRow(),
    createRow([
      "Metric",
      "Expected Value",
      "Counted",
      "Discrepancy",
      "Discrepancy %",
    ]),
    createRow([
      "Quantity",
      stockAudit.quantityExpected,
      stockAudit.quantityCounted,
      stockAudit.quantityCounted - stockAudit.quantityExpected,
      (
        ((stockAudit.quantityCounted - stockAudit.quantityExpected) /
          stockAudit.quantityExpected) *
        100
      ).toFixed(2) + "%",
    ]),
    createRow([
      "Stock Price",
      formatCurrency(stockAudit.stockPriceExpected, false, false),
      formatCurrency(stockAudit.stockPriceCounted, false, false),
      formatCurrency(
        stockAudit.stockPriceCounted - stockAudit.stockPriceExpected,
        false,
        false
      ),
      (
        ((stockAudit.stockPriceCounted - stockAudit.stockPriceExpected) /
          stockAudit.stockPriceExpected) *
        100
      ).toFixed(2) + "%",
    ]),
    createRow([
      "COGS",
      formatCurrency(stockAudit.cogsExpected, false, false),
      formatCurrency(stockAudit.cogsCounted, false, false),
      formatCurrency(
        stockAudit.cogsCounted - stockAudit.cogsExpected,
        false,
        false
      ),
      (
        ((stockAudit.cogsCounted - stockAudit.cogsExpected) /
          stockAudit.cogsExpected) *
        100
      ).toFixed(2) + "%",
    ]),
    createEmptyRow(),
    createEmptyRow(),
    createRow([`BREAKDOWN BY ${selectedBreakdown.toUpperCase()}`]),
    createEmptyRow(),
  ];

  // Convert breakdown data to use A, B, C, D, E, F... columns dynamically
  const normalizedBreakdownData = breakdownData.map((item) => {
    const values = Object.values(item);
    return createRow(values);
  });

  // Add breakdown header row
  const breakdownHeader = createRow(Object.keys(breakdownData[0] || {}));

  return [...summaryRows, breakdownHeader, ...normalizedBreakdownData];
};
const getConfirmCategoryCompletionHtmlTitle = () => (
  <h5 className="mt-2 ms-2">Confirm Category Audit Completion</h5>
);
const getConfirmCategoryCompletionHtml = () => {
  return `<div>
        <p>Completing this <strong>Category Audit</strong> will set the status to <strong>Complete</strong> and lock the <strong>Counted Qty</strong>
        values for each item in this Category. Before marking the <strong>Audit Session</strong> as complete, you can still revisit this Category
        and unlock all the items at any time by changing the status back to <strong>In Progress</strong>.</p>
        </div>
        <div style="padding-top: 5px;">
        <p style="text-align: center;"><strong>Would you like to proceed?</strong></p>
        </div>
        `;
};

const getConfirmAuditCompletionHtmlTitle = () => (
  <h5 className="mt-2 ms-2">Confirm Audit Session Completion</h5>
);
const getConfirmAuditCompletionHtmlShopify = () => {
  return `
        <div>
        <p>Completing this <strong>Category Session</strong> will automatically update inventory quantities for all counted items.
        The quantity of each item with a difference between <strong>Expected Quantity</strong> and <strong>Counted Quantity</strong> will be increased or 
        decreased accordingly. These changes will <strong>not</strong> be reversible, so please use caution before proceeding.</p>
        </div>
        <div >
        <p>While this update is running:</p>
        <ul>
          <li style="padding-top: 5px;">All <strong>inventory-related actions</strong> in CollectPOS (such as Sales, Trades, Returns, Adding or Editing Inventory, etc.)
          will be <strong>temporarily disabled</strong> to prevent conflicts.</li>
          <li style="padding-top: 5px;">Your <strong>connected Shopify Store's Website</strong> will also be <strong>temporarily unavailable</strong>.</li>
          <li style="padding-top: 5px;"><strong>No changes or edits</strong> should be made directly <strong>on Shopify</strong> during the update process to avoid interuptions.</li>
        </ul>
        </div>
        <div style="padding-top: 5px;">
        <p style="text-align: center;">It is highly recommended to only execute this action outside of buisness/operating hours.
        <strong>Are you sure you want to proceed?</strong></p>
        </div>
        `;
};
const getConfirmAuditCompletionHtml = () => {
  return `
        <div>
        <p>Completing this <strong>Category Session</strong> will automatically update inventory quantities for all counted items.
        The quantity of each item with a difference between <strong>Expected Quantity</strong> and <strong>Counted Quantity</strong> will be increased or 
        decreased accordingly. These changes will <strong>not</strong> be reversible, so please use caution before proceeding.</p>
        </div>
        <div >
        <p>While this update is running:</p>
        <ul>
          <li style="padding-top: 5px;">All <strong>inventory-related actions</strong> in CollectPOS (such as Sales, Trades, Returns, Adding or Editing Inventory, etc.)
          will be <strong>temporarily disabled</strong> to prevent conflicts.</li>
          </ul>
        </div>
        <div style="padding-top: 5px;">
        <p style="text-align: center;">It is highly recommended to only execute this action outside of buisness/operating hours.
        <strong>Are you sure you want to proceed?</strong></p>
        </div>
        `;
};

const getConfirmAuditCompletionIncompleteHtmlTitle = () => (
  <h5 className="mt-2 ms-2">
    Confirm Audit Session Completion –{" "}
    <span className="text-danger">Incomplete Categories Detected</span>
  </h5>
);
const getConfirmAuditCompletionIncompleteHtml = (
  totalCategories,
  completedCategories
) => {
  return `
        <div style="display: flex; justify-content: space-between; font-weight: bold; padding-bottom: 20px; border-bottom: 2px solid #000;">
          <div>Total Categories: <span>${totalCategories}</span></div>
          <div>Completed Categories: <span>${completedCategories}</span></div>
          <div>Incomplete Categories: <span style="color:#d32f2f;">${
            totalCategories - completedCategories
          }</span></div>
        </div>

        <div style="padding-top: 15px;">
        <p>It looks like there are still some <strong>Category Audits</strong> which have <strong>not yet</strong> been marked as <strong>Complete</strong>.
        Before you are able to proceed, you will need to go back and complete all remaining category Audits.</p>
        </div>
        `;
};

const getStockAuditBreakdownGridData = (stockAuditBreakdown, breakdownBy) => {
  const rows =
    breakdownBy === BREAKDOWN_ENUMS.ITEM.value
      ? stockAuditBreakdown?.inventory || []
      : stockAuditBreakdown?.stockAuditItemOrganization || [];
  return rows.map((row) => {
    const expectedQuantity = row?.totalQuantity ?? 0;
    const countedQuantity = row?.countedQuantity ?? 0;

    const expectedStockPrice = row?.totalStockPrice ?? 0;
    const countedStockPrice = row?.countedStockPrice ?? 0;

    const expectedCogs = row?.totalCogs ?? 0;
    const countedCogs = row?.countedCogs ?? 0;

    const buildMetric = (metric, expected, counted) => ({
      metric,
      expected,
      counted,
      discrepancy: parseFloat((counted - expected).toFixed(2)),
      discrepancyPercent: expected
        ? parseFloat(((counted - expected) * 100) / expected).toFixed(2)
        : 0,
    });

    return {
      ...row,
      expandedData: row?.expandedData?.length
        ? row.expandedData
        : [
            buildMetric("Quantity", expectedQuantity, countedQuantity),
            buildMetric("Stock Price", expectedStockPrice, countedStockPrice),
            buildMetric("COGS", expectedCogs, countedCogs),
          ],
    };
  });
};
const initializeLocalStorageValues = () => {
  setItemToLocalStorage(
    inventoryConstants.STOCK_AUDIT_INVENTORY_UPDATED_COUNT,
    0
  );
  setItemToLocalStorage(
    inventoryConstants.STOCK_AUDIT_INVENTORY_QUANTITY_DELTA,
    0
  );
  setItemToLocalStorage(inventoryConstants.STOCK_AUDIT, JSON.stringify([]));
};

const getLocalStorageStockAuditObject = (id, countedQuantity) => {
  return {
    id: id,
    price: {
      countedQuantity: countedQuantity,
    },
  };
};

const updateCountedQuantityOfObject = (object, countedQuantity) => {
  return {
    ...object,
    price: {
      ...object.price,
      countedQuantity: countedQuantity,
    },
  };
};

const getBreakdownTotalLabel = (selectedBreakdown) => {
  switch (selectedBreakdown) {
    case BREAKDOWN_ENUMS.TYPE.value:
      return "Total (Types):";
    case BREAKDOWN_ENUMS.CATEGORY.value:
      return "Total (Categories):";
    case BREAKDOWN_ENUMS.SUBCATEGORY.value:
      return "Total (SubCategories):";
    case BREAKDOWN_ENUMS.ITEM.value:
      return "Total (Items):";
    default:
      return "Total:";
  }
};

const getConfirmCategoryCompletionIncompleteHtmlTitle = (categoryCount) => {
  const isBulk = typeof categoryCount === "number";
  return (
    <h5 className="mt-2 ms-2">
      {isBulk
        ? "Confirm Bulk Category Audit Completion"
        : "Confirm Category Audit Completion"}{" "}
      –{" "}
      <span className="text-danger">
        Uncounted Items Detected
        {isBulk ? ` (${categoryCount} Categories Selected)` : ""}
      </span>
    </h5>
  );
};

const getConfirmCategoryCompletionIncompleteHtml = (
  categoryNameOrCount,
  totalItems,
  countedItems
) => {
  const isBulk = typeof categoryNameOrCount === "number";

  const header = isBulk
    ? `<div>Selected Categories: <span>${categoryNameOrCount}</span></div>
       <div>Total Items: <span>${totalItems}</span></div>
       <div>Counted Items: <span>${countedItems}</span></div>
       <div>Uncounted Items: <span style="color:#d32f2f;">${
         totalItems - countedItems
       }</span></div>`
    : `<div>Category Name: <span>${categoryNameOrCount}</span></div>
       <div>Total Items: <span>${totalItems}</span></div>
       <div>Counted Items: <span>${countedItems}</span></div>
       <div>Uncounted Items: <span style="color:#d32f2f;">${
         totalItems - countedItems
       }</span></div>`;

  const intro = isBulk
    ? `some of the selected <strong>categories</strong> may contain <strong>items</strong> that <strong>were not counted</strong> during this audit.`
    : `there are some <strong>items</strong> in this category that <strong>were not counted</strong> during this audit.`;

  const subject = isBulk ? "these Categories" : "this Category";

  return `
    <div style="display: flex; justify-content: space-between; font-weight: bold; padding-bottom: 20px; border-bottom: 2px solid #000;">
      ${header}
    </div>
    <div style="padding-top: 15px;">
      <p>It looks like ${intro}</p>
      <p>Before you can mark ${subject} as <strong>Complete</strong>, please choose how you want to handle uncounted items. This choice will affect how inventory is updated when the overall <strong>Audit Session</strong> is completed.</p>
      <p>Option 1: <strong>Ignore</strong> all uncounted items.</p>
      <ul>
        <li>The <strong>Counted Qty</strong> for each uncounted item will be set to the same value as its Expected Qty.</li>
        <li>Upon completion of the <strong>Audit Session</strong>, Inventory quantities for uncounted items will remain as they were before the audit.</li>
      </ul>
      <p>Option 2: Set uncounted items to <strong>Zero</strong>.</p>
      <ul>
        <li>The <strong>Counted Qty</strong> for each uncounted item will be set to 0.</li>
        <li>Upon completion of the <strong>Audit Session</strong>, Inventory quantities for uncounted items will be set to 0.</li>
      </ul>
    </div>`;
};

export {
  getAuditExpandedRow,
  getStockAuditColumns,
  convertDataToDisplayable,
  initializeLocalStorageValues,
  getStockAuditInventoryColumns,
  getConfirmAuditCompletionHtml,
  updateCountedQuantityOfObject,
  getLocalStorageStockAuditObject,
  getConfirmCategoryCompletionHtml,
  getConfirmAuditCompletionHtmlTitle,
  getConfirmAuditCompletionHtmlShopify,
  getConfirmCategoryCompletionHtmlTitle,
  getConfirmAuditCompletionIncompleteHtml,
  getConfirmAuditCompletionIncompleteHtmlTitle,
  getStockAuditBreakdownExpandedRow,
  getStockAuditCategoriesColumns,
  getStockAuditBreakdownColumns,
  getStockAuditBreakdownCSVColumns,
  getConfirmCategoryCompletionIncompleteHtml,
  getConfirmCategoryCompletionIncompleteHtmlTitle,
  convertExpandedDataToDisplayable,
  getStockAuditBreakdownGridData,
  getBreakdownTotalLabel,
  getStockAuditCSVWithSummary,
};
